import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock, useModalClose } from '@/common/hooks';
import { CloseButton } from './IconButton';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const EXEMPT_OVERLAY =
  '[data-portal-dropdown="true"], .react-datepicker-popper, .react-datepicker';

/** Nested drawers: only the top one traps focus. */
const openDrawers = [];
let inertCount = 0;

function isExemptOverlay(node) {
  return node instanceof Element && Boolean(node.closest(EXEMPT_OVERLAY));
}

function isHidden(el) {
  if (el.disabled || el.getAttribute('aria-disabled') === 'true') return true;
  if (el.closest('[hidden], [inert]')) return true;
  if (el.getAttribute('aria-hidden') === 'true') return true;
  const style = window.getComputedStyle(el);
  return style.display === 'none' || style.visibility === 'hidden';
}

function getFocusable(root) {
  if (!root) return [];
  return [...root.querySelectorAll(FOCUSABLE)].filter(
    (el) => el.tabIndex >= 0 && !isHidden(el),
  );
}

function isEditableField(el) {
  if (el.readOnly || el.disabled) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (el.getAttribute('type') || 'text').toLowerCase();
    return !['hidden', 'button', 'submit', 'reset', 'file'].includes(type);
  }
  return el.isContentEditable;
}

function lockAppRoot() {
  const root = document.getElementById('root');
  inertCount += 1;
  if (root) root.setAttribute('inert', '');
}

function unlockAppRoot() {
  const root = document.getElementById('root');
  inertCount = Math.max(0, inertCount - 1);
  if (inertCount === 0 && root) root.removeAttribute('inert');
}

function restoreFocus(el) {
  const root = document.getElementById('root');
  if (root?.hasAttribute('inert')) return;
  if (
    el instanceof HTMLElement &&
    el.isConnected &&
    !el.disabled &&
    !isHidden(el)
  ) {
    el.focus();
    return;
  }
  getFocusable(root)[0]?.focus();
}

const WIDTH_CLASSES = {
  sm: 'sm:max-w-[420px]',
  md: 'sm:max-w-[480px]',
  lg: 'sm:max-w-md lg:max-w-2xl',
  xl: 'sm:max-w-3xl',
  settings: 'sm:max-w-[960px]',
};

const ANIMATION_MS = 300;
const PANEL_RADIUS = 'rounded-[24px]';

/**
 * Floating right slide-over drawer (20px inset, 24px radius).
 * Open/close: slides from the right + fades backdrop (no jump).
 */
export function SideDrawer({
  isOpen,
  onClose,
  children,
  title,
  description,
  header,
  footer,
  width = 'md',
  showClose = true,
  isDirty = false,
  className = '',
  bodyClassName = '',
  headerClassName = '',
  footerClassName = '',
  fitContent = false,
  zIndexClass = 'z-[100]',
  onBodyScroll,
  closeOnOutside = true,
  labelledBy,
}) {
  const [mounted, setMounted] = useState(false);
  /** Drives CSS transition — lags one frame behind isOpen so enter animates */
  const [visible, setVisible] = useState(false);
  const titleId = useId();
  const returnFocusRef = useRef(null);
  const wasOpenRef = useRef(false);

  useBodyScrollLock(isOpen);

  const panelRef = useModalClose({
    isOpen,
    onClose,
    isDirty,
    closeOnOutside,
  });

  useEffect(() => {
    if (!isOpen) {
      if (wasOpenRef.current) {
        wasOpenRef.current = false;
        const previous = returnFocusRef.current;
        returnFocusRef.current = null;
        restoreFocus(previous);
      }
      return undefined;
    }

    wasOpenRef.current = true;
    const active = document.activeElement;
    if (!panelRef.current?.contains(active)) {
      returnFocusRef.current = active instanceof HTMLElement ? active : null;
    }

    const token = {};
    openDrawers.push(token);
    lockAppRoot();

    const isTop = () => openDrawers[openDrawers.length - 1] === token;

    const focusInitial = () => {
      const panel = panelRef.current;
      if (!panel || !isTop()) return;
      const items = getFocusable(panel);
      const target = items.find(isEditableField) || items[0];
      target?.focus();
    };

    const raf = requestAnimationFrame(focusInitial);

    const onKeyDown = (e) => {
      if (e.key !== 'Tab' || !isTop()) return;
      if (isExemptOverlay(e.target)) return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = getFocusable(panel);
      if (!items.length) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!panel.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const onFocusIn = (e) => {
      if (!isTop() || isExemptOverlay(e.target)) return;
      const panel = panelRef.current;
      if (!panel || panel.contains(e.target)) return;
      const items = getFocusable(panel);
      const target = items.find(isEditableField) || items[0];
      target?.focus();
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('focusin', onFocusIn);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocusIn);
      const index = openDrawers.indexOf(token);
      if (index > -1) openDrawers.splice(index, 1);
      unlockAppRoot();
    };
  }, [isOpen, panelRef]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    if (isOpen) {
      setMounted(true);
      // Paint closed (off-screen) first, then slide in on the next frames
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setVisible(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }

    setVisible(false);
    const timer = setTimeout(() => setMounted(false), ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  if ((!isOpen && !mounted) || typeof document === 'undefined') return null;

  const hasHeader = Boolean(header || title || showClose);
  const dialogLabel = labelledBy || (title ? titleId : undefined);
  const hasFooter = Boolean(footer);

  const handleOverlayMouseDown = (e) => {
    e.stopPropagation();
  };

  const handleOverlayClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDirty && closeOnOutside) onClose?.();
  };

  const content = (
    <div
      className={`fixed inset-0 ${zIndexClass} overflow-hidden font-sf-pro-display`}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ease-in-out ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        aria-hidden="true"
        onMouseDown={handleOverlayMouseDown}
        onPointerDown={handleOverlayMouseDown}
        onClick={handleOverlayClick}
      />

      <aside
        ref={panelRef}
        data-drawer-root
        role="dialog"
        aria-modal="true"
        aria-labelledby={dialogLabel}
        onMouseDown={(e) => e.stopPropagation()}
        className={`fixed z-50 right-2 left-2 sm:left-auto sm:right-5 w-auto sm:w-[calc(100%-2.5rem)] ${
          fitContent
            ? 'top-2 sm:top-5 bottom-auto h-auto max-h-[calc(100vh-16px)] sm:max-h-[calc(100vh-40px)]'
            : 'top-2 bottom-2 sm:top-5 sm:bottom-5 h-auto max-h-[calc(100vh-16px)] sm:max-h-[calc(100vh-40px)]'
        } ${WIDTH_CLASSES[width] || WIDTH_CLASSES.md} ${PANEL_RADIUS} border border-[#EBEBEB] bg-white shadow-[0_16px_48px_rgba(0,0,0,0.16)] flex flex-col overflow-hidden will-change-transform transition-transform duration-300 ease-in-out ${
          visible ? 'translate-x-0' : 'translate-x-[calc(100%+20px)]'
        } ${className}`}
      >
        {hasHeader && (
          <div
            className={`shrink-0 border-b border-[#EBEBEB] bg-white rounded-t-[24px] ${headerClassName || 'px-4 py-4 sm:px-6 sm:py-5'} ${
              header ? '' : 'flex items-center justify-between gap-3'
            }`}
          >
            {header || (
              <>
                <div className="min-w-0 flex-1">
                  {title && (
                    <h2
                      id={title ? titleId : undefined}
                      className="text-base font-bold text-text-primary truncate"
                    >
                      {title}
                    </h2>
                  )}
                  {description && (
                    <p className="text-xs text-text-muted mt-0.5">
                      {description}
                    </p>
                  )}
                </div>
                {showClose && onClose && <CloseButton onClick={onClose} />}
              </>
            )}
          </div>
        )}

        <div
          className={`${fitContent ? 'min-h-0 shrink overflow-y-auto' : 'flex-grow min-h-0'} ${bodyClassName?.includes('overflow-hidden') && !fitContent ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'} ${
            bodyClassName?.includes('visible-scrollbar') ||
            bodyClassName?.includes('stage-column-scrollbar') ||
            bodyClassName?.includes('custom-scrollbar')
              ? ''
              : 'no-scrollbar'
          } bg-white ${!hasHeader ? 'rounded-t-[24px]' : ''} ${!hasFooter ? 'rounded-b-[24px]' : ''} ${bodyClassName || 'p-4 sm:p-6'}`}
          onScroll={onBodyScroll}
        >
          {children}
        </div>

        {hasFooter && (
          <div
            className={`shrink-0 border-t border-[#EBEBEB] bg-white rounded-b-[24px] px-4 py-4 sm:px-6 sm:py-5 ${footerClassName}`}
          >
            {footer}
          </div>
        )}
      </aside>
    </div>
  );

  return createPortal(content, document.body);
}
